/**
 * Employee tagging: the screen where HR gives a person their department,
 * designation, role, reporting head, office and employment details.
 *
 * Route: /my-workspace/employee-tagging/:id
 * Reached from the Edit button on the all users table.
 *
 * Replaces the old /my-workspace/editemployee/:id screen, which is still routed
 * and still works, it is simply no longer linked to.
 *
 * Laid out with GlobalWrapper, the same as every other page here, so the title,
 * breadcrumb, transition and page padding all match. Do not add Page or Container.
 *
 * THREE THINGS THIS SCREEN DOES THAT THE OLD ONE DID NOT
 *   1. Employee ID, exit date and exit reason, which had no field at all before
 *   2. Conditional fields, copied from the PHP screen because the rules are right:
 *        Probation Completed On  shows when Employment Type is Permanent
 *        Exit Date and Reason    show when status is Resigned or Terminated
 *   3. A confirmation popup before an exit and before a rejoin, because both are
 *      recorded permanently and a rejoin clears fields
 *
 * See the node portal repo, docs/user-role-tag-with-history/README.md
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';

import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import Loader3D from '../../../../components/Loader/Loader3D';
import apiRequest from '../../../../api/request';

/** employment_statuses ids. 2 Resigned and 3 Terminated mean the person is leaving. */
const LEAVING_STATUSES = [2, 3];
/** employment_types. Probation Completed On only applies once somebody is Permanent. */
const PERMANENT_TYPE = 1;

/** dd/mm/yyyy for reading. The API speaks yyyy-mm-dd. */
const showDate = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

/** yyyy-mm-dd, which is what a date input and the API both want. */
const inputDate = (value) => (value ? String(value).slice(0, 10) : '');

export default function EmployeeTagging() {
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);

  const [form, setForm] = useState(null);
  // what the status was when the screen loaded, so a rejoin can be spotted
  const [statusOnLoad, setStatusOnLoad] = useState(null);

  const [confirm, setConfirm] = useState(null); // 'EXIT' | 'REJOIN' | null
  const [pendingStatus, setPendingStatus] = useState(null);

  /**
   * The row contradicts itself: it carries an exit date, but its status says the
   * person is working. Both cannot be true, and no code can tell which one is.
   * Only HR knows, so the screen asks once and will not save until they answer.
   *
   *   null       not answered yet, saving is blocked
   *   'WRONG'    the exit date was never real, take it off
   *   'REJOINED' they really did leave and came back
   */
  const [answered, setAnswered] = useState(null);

  /**
   * The joining date of their EARLIER employment, typed in by hand.
   *
   * Only asked for when it cannot be recovered: somebody has already reused this
   * row for a rejoin, so the joining date on it is that rejoin date and the real
   * first one is gone. Without it their earlier employment cannot be written at
   * all, and their history would say they worked here once when they worked here
   * twice. Optional, because a date HR does not know is worse than no date.
   */
  const [previousJoinedOn, setPreviousJoinedOn] = useState('');

  // Which department and designation the two cascade lists already hold. Without
  // this the effects below would fetch again straight after load() has already
  // done it, for the same ids.
  const loadedDept = useRef(null);
  const loadedDesignation = useRef(null);

  /* ---- everything the screen needs, in one call -------------------------- */
  /**
   * `silent` means: go and fetch fresh data, but do NOT put the page back into its
   * loading state. Used straight after a save, where a loader is already on screen.
   * Without it the page dropped back to the loading branch for a moment, and that
   * branch shows no content, which is the blank screen between loader and form.
   */
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const res = await apiRequest({ method: 'get', url: `users/employee-tagging/${params.id}` });

    if (res?.status === 'success') {
      const { user, currentOrg } = res.data;

      // The person's own designation and role lists are fetched HERE, before the
      // form is shown, not by the effects below. Those only run once the form
      // exists, so the screen would appear with Designation and Role empty and
      // they would pop in a moment later.
      const [desigRes, roleRes] = await Promise.all([
        currentOrg?.departmentId
          ? apiRequest({ method: 'get', url: `users/employee-tagging/designations/${currentOrg.departmentId}` })
          : Promise.resolve(null),
        currentOrg?.designationId
          ? apiRequest({ method: 'get', url: `users/employee-tagging/roles/${currentOrg.designationId}` })
          : Promise.resolve(null),
      ]);
      setDesignations(desigRes?.status === 'success' ? desigRes.data.designations : []);
      setRoles(roleRes?.status === 'success' ? roleRes.data.roles : []);
      loadedDept.current = currentOrg?.departmentId ?? null;
      loadedDesignation.current = currentOrg?.designationId ?? null;

      setData(res.data);
      setStatusOnLoad(user.employmentStatusId);
      setForm({
        departmentId: currentOrg?.departmentId ?? '',
        // '' means "All wings", which keeps the 38 designations that have no wing
        // reachable. 82 people already sit on one of them.
        wingId: currentOrg?.wingId ?? '',
        designationId: currentOrg?.designationId ?? '',
        roleId: currentOrg?.roleId ?? '',
        joinedOn: inputDate(user.joinedOn),
        reportingHeadId: user.reportingHeadId > 0 ? user.reportingHeadId : '',
        employmentTypeId: user.employmentTypeId ?? '',
        employmentStatusId: user.employmentStatusId ?? '',
        loginStatus: user.loginStatus ?? 1,
        empId: user.empId ? String(user.empId).toUpperCase() : '',
        officeId: user.officeId ?? '',
        probationCompletedOn: inputDate(user.probationCompletedOn),
        exitDate: inputDate(user.exitDate),
        exitReasonId: user.exitReasonId ?? '',
      });
      // Prefilled where the row still holds the FIRST joining date, which is the
      // case whenever it is earlier than the exit date. Where it is not, the row's
      // date is a later rejoin date and would be a wrong answer, so it is left
      // empty for HR to supply.
      setPreviousJoinedOn(
        res.data?.employment?.canRebuildOldSpell
          ? inputDate(res.data.employment.joiningDateOnRow)
          : '',
      );
      setError('');
    } else {
      setError(res?.message || 'Could not load this employee');
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // Ask the moment the screen opens, not when HR presses Save. If they are only
  // here to fix a phone number they still need to know the row disagrees with
  // itself, and answering costs one click.
  useEffect(() => {
    if (data?.employment?.contradictsItself && !answered) setConfirm('CONTRADICTION');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /* ---- the two cascade lists --------------------------------------------- */
  useEffect(() => {
    if (!form?.departmentId) { setDesignations([]); loadedDept.current = null; return; }
    // load() already fetched this one
    if (loadedDept.current === form.departmentId) return;
    loadedDept.current = form.departmentId;
    apiRequest({
      method: 'get',
      url: `users/employee-tagging/designations/${form.departmentId}`,
    }).then((res) => setDesignations(res?.status === 'success' ? res.data.designations : []));
  }, [form?.departmentId]);

  useEffect(() => {
    if (!form?.designationId) { setRoles([]); loadedDesignation.current = null; return; }
    if (loadedDesignation.current === form.designationId) return;
    loadedDesignation.current = form.designationId;
    apiRequest({
      method: 'get',
      url: `users/employee-tagging/roles/${form.designationId}`,
    }).then((res) => setRoles(res?.status === 'success' ? res.data.roles : []));
  }, [form?.designationId]);

  /**
   * The wings available in the chosen department, worked out from the designation
   * list we already have. No extra API call, and it only offers wings that actually
   * contain a designation.
   */
  const wings = useMemo(() => {
    const seen = new Map();
    designations.forEach((d) => {
      if (d.wingId && !seen.has(d.wingId)) seen.set(d.wingId, d.wingName);
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [designations]);

  /**
   * Designations shown in the dropdown. With no wing chosen the full list is
   * offered, INCLUDING the ones that have no wing. That is deliberate: making the
   * wing compulsory would leave 82 people unable to keep their own current role.
   */
  const visibleDesignations = useMemo(
    () => (form?.wingId ? designations.filter((d) => d.wingId === form.wingId) : designations),
    [designations, form?.wingId],
  );

  /* ---- which conditional fields are showing ------------------------------ */
  const isLeaving = LEAVING_STATUSES.includes(Number(form?.employmentStatusId));
  const isPermanent = Number(form?.employmentTypeId) === PERMANENT_TYPE;

  /**
   * Is this save a rejoin? Only when the person has actually left before AND the
   * status is being moved off Resigned or Terminated.
   *
   * Leaving is judged FIRST elsewhere, so editing an exited person's phone number
   * can never look like a rejoin.
   */
  const isRejoining = useMemo(
    () =>
      Boolean(data) &&
      !data.employment.hasOpenSpell &&
      data.employment.everWorked &&
      !isLeaving &&
      // A row that contradicted itself only counts as a rejoin once HR says it is
      // one. When they answer that the exit date was simply wrong, this person
      // never left, so there is no previous exit for the joining date to be after.
      // Without this, correcting a mistake was refused for a date that is about to
      // be deleted as never having happened.
      answered !== 'WRONG',
    [data, isLeaving, answered],
  );

  /* ---- changing the employment status ------------------------------------ */
  const onStatusChange = (value) => {
    const leavingNow = LEAVING_STATUSES.includes(Number(value));
    const wasLeaving = LEAVING_STATUSES.includes(Number(statusOnLoad));

    // moving OFF Resigned or Terminated on somebody who has left = a rejoin.
    // Ask before clearing anything, so a mis-click costs nothing.
    if (wasLeaving && !leavingNow && data?.employment?.everWorked && !data?.employment?.hasOpenSpell) {
      setPendingStatus(value);
      setConfirm('REJOIN');
      return;
    }

    // no longer leaving, so the exit fields go away and must not keep stale values
    if (!leavingNow) {
      setForm({ ...form, employmentStatusId: value, exitDate: '', exitReasonId: '' });
      return;
    }

    // Somebody who has left should not still be able to sign in. Logging in is
    // decided by user.active alone, never by the employment status, so unless this
    // is switched off here a leaver keeps their access. It is a default and not a
    // rule: HR can turn it straight back on for somebody serving notice who still
    // needs a few more days, and the exit popup says plainly which way it is set.
    setForm({ ...form, employmentStatusId: value, loginStatus: 0 });
  };

  /** HR confirmed the rejoin. Clear what a new spell must not inherit. */
  const applyRejoin = () => {
    setForm({
      ...form,
      employmentStatusId: pendingStatus,
      joinedOn: '',              // a new spell needs its own start date
      probationCompletedOn: '',  // probation starts again
      exitDate: '',
      exitReasonId: '',
    });
    setPendingStatus(null);
    setConfirm(null);
    // On a row that contradicted itself, confirming here IS the answer.
    if (data?.employment?.contradictsItself) setAnswered('REJOINED');
  };

  /** HR says the exit date was never real. Nothing else about the row changes. */
  const answerExitDateWrong = () => {
    setAnswered('WRONG');
    setForm({ ...form, exitDate: '', exitReasonId: '' });
    setConfirm(null);
  };

  /**
   * HR says they really left and came back.
   *
   * Nothing is cleared here. It hands over to the ordinary rejoin confirmation,
   * which is the popup that lists exactly what will be wiped and what has to be
   * entered again. Clearing the form the moment this is clicked left HR staring at
   * empty boxes with nothing on screen saying why, which is what happened the
   * first time this was tried.
   *
   * The status is passed through unchanged, because on one of these rows it
   * already says Working. There is nothing to switch.
   */
  const answerRejoined = () => {
    setPendingStatus(form.employmentStatusId);
    setConfirm('REJOIN');
  };

  /**
   * Backing out of the rejoin confirmation. On a row that contradicts itself the
   * question still has to be answered, so this goes BACK to it rather than leaving
   * the screen with no popup and a Save button that refuses to work.
   */
  const cancelRejoin = () => {
    setPendingStatus(null);
    if (data?.employment?.contradictsItself && !answered) setConfirm('CONTRADICTION');
    else setConfirm(null);
  };

  /* ---- validation, the same rules the server applies again --------------- */
  const validate = () => {
    if (data?.employment?.contradictsItself && !answered) {
      return 'Please answer the question about the exit date on this record first';
    }
    if (!form.roleId) return 'Please choose a role';
    if (!form.employmentStatusId) return 'Please choose an employment status';
    if (!form.employmentTypeId) return 'Please choose an employment type';
    if (!form.officeId) return 'Please choose an office';
    if (!form.empId) return 'Employee ID is required';

    if (isLeaving && !form.exitDate) return 'Please enter the exit date';
    if (isLeaving && form.joinedOn && form.exitDate < form.joinedOn) {
      return 'Exit date cannot be before the joining date';
    }
    if (!form.joinedOn) return 'Please enter the date of joining';

    if (isRejoining) {
      const lastExit = data.employment.lastExitDate;
      if (lastExit && form.joinedOn <= lastExit) {
        return `Joining date must be after the previous exit date, ${showDate(lastExit)}`;
      }
    }
    if (form.probationCompletedOn && form.joinedOn && form.probationCompletedOn < form.joinedOn) {
      return 'Probation completed date cannot be before the joining date';
    }
    return null;
  };

  const doSave = async () => {
    setSaving(true);
    const res = await apiRequest({
      method: 'patch',
      url: `users/employee-tagging/${params.id}`,
      data: {
        joinedOn: form.joinedOn || null,
        roleId: form.roleId,
        reportingHeadId: form.reportingHeadId || null,
        employmentTypeId: form.employmentTypeId,
        employmentStatusId: form.employmentStatusId,
        loginStatus: form.loginStatus,
        empId: form.empId,
        officeId: form.officeId,
        probationCompletedOn: isPermanent ? form.probationCompletedOn || null : null,
        exitDate: isLeaving ? form.exitDate || null : null,
        exitReasonId: isLeaving ? form.exitReasonId || null : null,
        // Only ever true when HR was asked and said the exit date was recorded in
        // error. Without it the server treats an exit date on the row as a real
        // one and expects a rejoin, which is the safer of the two readings.
        exitDateWasWrong: answered === 'WRONG',
        previousJoinedOn: previousJoinedOn || null,
      },
    });
    setConfirm(null); // close the popup, but leave the loader up

    if (res?.status === 'success') {
      const what = {
        FIRST_SAVE: 'Saved. Employment history has been started for this person.',
        EDIT: 'Saved.',
        EXIT: 'Saved. The employment record has been closed.',
        REJOIN: 'Saved. A new employment record has been created.',
        EXITED_NO_HISTORY: 'Saved. No employment history is kept for this person.',
        EDIT_EXITED: 'Saved. Their record has been corrected.',
        REJOIN_FROM_ROW: res.data?.rebuiltOldSpell
          ? 'Saved. Their earlier employment was recorded, and a new record started.'
          : 'Saved. A new employment record has been created.',
      };
      enqueueSnackbar(what[res.data?.action] || 'Saved.', { variant: 'success' });
      // Refreshed underneath the same loader, so it reads as one loader from the
      // click until the fresh form appears, with no blank page in between.
      await load({ silent: true });
    } else {
      enqueueSnackbar(res?.message || 'Could not save', { variant: 'error' });
    }
    setSaving(false);
  };

  const onSaveClick = () => {
    const problem = validate();
    if (problem) {
      enqueueSnackbar(problem, { variant: 'warning' });
      return;
    }
    // an exit is permanent, so confirm it. A rejoin was already confirmed when the
    // status changed, so it saves straight through.
    if (isLeaving) {
      setConfirm('EXIT');
      return;
    }
    doSave();
  };

  /**
   * Show History opens in a NEW TAB, the same as the button on the all users list,
   * so whatever HR has half filled in on this form is not lost.
   */
  const pageActions = (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<Icon icon="mdi:history" />}
        onClick={() =>
          window.open(`/my-workspace/employee-history/${params.id}`, '_blank', 'noopener')
        }
      >
        Show History
      </Button>
      <Button size="small" variant="outlined" onClick={() => navigate('/my-workspace/all-users')}>
        Back
      </Button>
    </Stack>
  );

  if (error) {
    return (
      <GlobalWrapper title="Employee Tagging" actions={pageActions} skelton={false}>
        <Alert severity="error">{error}</Alert>
      </GlobalWrapper>
    );
  }
  // One loader covers both the first load and a save. GlobalWrapper's own skeleton
  // only lasts a second and then leaves an empty page behind, so the loader here is
  // ours and it stays up until there is actually a form to show.
  if (loading || saving || !data || !form) {
    return (
      <GlobalWrapper title="Employee Tagging" actions={pageActions} skelton={false}>
        <Loader3D />
      </GlobalWrapper>
    );
  }

  const { user, currentOrg, employment, lookups } = data;
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <GlobalWrapper title="Employee Tagging" actions={pageActions} skelton={false}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h6">{user.name}</Typography>
          {user.empId && (
            <Typography variant="body2" color="text.secondary">
              {String(user.empId).toUpperCase()}
            </Typography>
          )}
        </Stack>

        {/* This person left before any of this existed. We never saw a single day
            of their employment, so we record none of it rather than invent it. */}
        {employment.exitedWithoutHistory && (
          <Alert severity="warning" icon={<Icon icon="mdi:account-off-outline" />}>
            <Typography variant="subtitle2">
              No employment history is kept for this person
            </Typography>
            <Typography variant="body2">
              They exited on <strong>{showDate(employment.exitDateOnRow)}</strong>,
              before any history was recorded. Their details can still be corrected
              here, and nothing will be written to their employment history. If they
              rejoin, set the status to Working and it will be recorded properly.
            </Typography>
          </Alert>
        )}

        {/* The row disagrees with itself. HR answers once, on this screen. */}
        {employment.contradictsItself && (
          <Alert
            severity={answered ? 'success' : 'error'}
            icon={<Icon icon={answered ? 'mdi:check-circle-outline' : 'mdi:alert-outline'} />}
            action={
              !answered && (
                <Button size="small" variant="contained" color="error"
                  onClick={() => setConfirm('CONTRADICTION')}>
                  Answer
                </Button>
              )
            }
          >
            <Typography variant="subtitle2">
              {answered === 'WRONG' && 'The exit date will be removed on save'}
              {answered === 'REJOINED' && 'This will be saved as a rejoining'}
              {!answered && 'This record does not agree with itself'}
            </Typography>
            <Typography variant="body2">
              It carries an exit date of{' '}
              <strong>{showDate(employment.exitDateOnRow)}</strong>, but the status
              says this person is working.
              {!answered && ' Please say which one is correct before saving.'}
            </Typography>
          </Alert>
        )}

        {/* History has not started. Expected: we do not backfill, it begins on the
            first save, seeded from the joining date with the details already held. */}
        {employment.historyEmpty
          && !employment.exitedWithoutHistory
          && !employment.contradictsItself && (
          <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
            No employment history yet. Saving will start it from the joining date
            <strong> {showDate(user.joinedOn) || 'today'}</strong>, using the details
            already on record. Positions before that date were never recorded.
          </Alert>
        )}

        {/* The two banners above already say all of this for a row that carries its
            own exit date, so it is only shown when their leaving came from real
            recorded history. Two banners saying the same thing read as a fault. */}
        {!employment.hasOpenSpell && employment.everWorked
          && !employment.contradictsItself && !employment.exitedWithoutHistory && (
          <Alert severity="warning">
            This person left on <strong>{showDate(employment.lastExitDate)}</strong>
            {employment.lastExitReason ? ` · ${employment.lastExitReason}` : ''}.
            Changing the employment status will record a rejoining.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* read only, the employee maintains these on their own profile */}
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Full Name" disabled
                value={user.name || ''} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Mobile No." disabled
                value={user.phone ? `+${user.countryCode || ''}${user.phone}` : ''}
                InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* the cascade. Department and Designation only narrow the Role,
                which is the value actually saved. */}
            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Department *"
                value={form.departmentId}
                onChange={(e) => set({ departmentId: e.target.value, wingId: '', designationId: '', roleId: '' })}>
                {lookups.departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Wing"
                value={form.wingId} disabled={!form.departmentId}
                onChange={(e) => set({ wingId: e.target.value, designationId: '', roleId: '' })}
                helperText="Narrows the designations. Leave on All wings to see every one">
                <MenuItem value="">All wings</MenuItem>
                {wings.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Designation *"
                value={form.designationId} disabled={!form.departmentId}
                onChange={(e) => set({ designationId: e.target.value, roleId: '' })}
                helperText={
                  form.wingId
                    ? `${visibleDesignations.length} in this wing`
                    : 'Wing shown beside each, one job title can exist in several wings'
                }>
                {visibleDesignations.map((d) => (
                  <MenuItem key={d.designationId} value={d.designationId}>
                    {d.designationName}
                    {!form.wingId && (d.wingName ? ` · ${d.wingName}` : ' · no wing')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Role *"
                value={form.roleId} disabled={!form.designationId}
                onChange={(e) => set({ roleId: e.target.value })}
                helperText="This is what gets saved">
                {roles.map((r) => (
                  <MenuItem key={r.roleId} value={r.roleId}>{r.roleName}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Reporting Head"
                value={form.reportingHeadId}
                onChange={(e) => set({ reportingHeadId: e.target.value })}>
                {lookups.reportingHeads.map((h) => (
                  <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="Date of Joining *"
                InputLabelProps={{ shrink: true }} value={form.joinedOn}
                onChange={(e) => set({ joinedOn: e.target.value })}
                helperText={isRejoining ? 'Must be after the previous exit date' : ' '} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Employee ID *"
                value={form.empId}
                onChange={(e) => set({ empId: e.target.value.toUpperCase() })} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Employment Type *"
                value={form.employmentTypeId}
                onChange={(e) => set({ employmentTypeId: e.target.value })}>
                {lookups.employmentTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Employment Status *"
                value={form.employmentStatusId}
                onChange={(e) => onStatusChange(e.target.value)}>
                {lookups.employmentStatuses.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Login Status *"
                value={form.loginStatus}
                onChange={(e) => set({ loginStatus: e.target.value })}>
                {lookups.loginStatuses.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Office *"
                value={form.officeId}
                onChange={(e) => set({ officeId: e.target.value })}>
                {lookups.offices.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* CONDITIONAL. Shows once somebody is Permanent, because that is when
                you record the date their probation finished. */}
            {isPermanent && (
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" type="date" label="Probation Completed On"
                  InputLabelProps={{ shrink: true }} value={form.probationCompletedOn}
                  onChange={(e) => set({ probationCompletedOn: e.target.value })} />
              </Grid>
            )}

            {/* CONDITIONAL. Shows for Resigned and Terminated only. Unlike the PHP
                screen, the exit date is REQUIRED here: 711 resigned people have no
                exit date because it was optional there. */}
            {isLeaving && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" type="date" label="Exit Date *"
                    InputLabelProps={{ shrink: true }} value={form.exitDate}
                    onChange={(e) => set({ exitDate: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth size="small" label="Exit Reason"
                    value={form.exitReasonId}
                    onChange={(e) => set({ exitReasonId: e.target.value })}>
                    {lookups.exitReasons.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined"
                  label={`Currently: ${currentOrg ? currentOrg.roleName : 'no role tagged'}`} />
                {currentOrg?.wingName && (
                  <Chip size="small" variant="outlined" label={`Wing: ${currentOrg.wingName}`} />
                )}
                {employment.spellCount > 1 && (
                  <Chip size="small" color="warning" variant="outlined"
                    label={`${employment.spellCount} employment records`} />
                )}
              </Stack>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="contained" onClick={onSaveClick} disabled={saving}
              startIcon={<Icon icon={saving ? 'mdi:loading' : 'mdi:content-save'} />}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Paper>
      </Stack>

      {/* ---------------- popup 0, the row disagrees with itself ----------- */}
      {/* It closes, because somebody who came here to fix a phone number should not
          be trapped by a question they may have to go and ask about. What it does
          NOT do is let the answer be skipped: saving stays blocked, and the red
          banner behind carries an Answer button that brings this back. */}
      <Dialog open={confirm === 'CONTRADICTION'} onClose={() => setConfirm(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Which of these is correct?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{user.name}</strong> has an exit date recorded, but their status
            says they are working. Both cannot be true.
          </Typography>

          <Box sx={{ my: 2 }}>
            <Typography variant="body2">
              Date of joining &nbsp;
              <strong>{showDate(employment.joiningDateOnRow) || 'not recorded'}</strong>
            </Typography>
            <Typography variant="body2">
              Exit date &nbsp;<strong>{showDate(employment.exitDateOnRow)}</strong>
            </Typography>
            <Typography variant="body2">
              Status &nbsp;
              <strong>
                {lookups.employmentStatuses.find(
                  (x) => x.id === Number(form.employmentStatusId),
                )?.name || 'not set'}
              </strong>
            </Typography>
          </Box>

          <Alert severity="info" icon={<Icon icon="mdi:lightbulb-outline" />}>
            <Typography variant="body2">
              <strong>The exit date is wrong</strong> means this person never
              stopped working and the date was entered by mistake. It is removed,
              and a note records that it was removed and by whom.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>They left and rejoined</strong> means they really did leave on
              that date and have come back. You will be asked for the new joining
              date, and probation starts again.
              {employment.canRebuildOldSpell
                ? ' Their earlier employment will be recorded as a closed record.'
                : ' Their original joining date is no longer held anywhere, so you'
                  + ' will be asked for it, and their earlier employment will be'
                  + ' recorded from it. It is required.'}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={answerExitDateWrong}>The exit date is wrong</Button>
          <Button variant="contained" onClick={answerRejoined}>
            They left and rejoined
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- popup 1, confirming an exit ---------------------- */}
      <Dialog open={confirm === 'EXIT'} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm exit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            You are recording that <strong>{user.name}</strong> has left.
          </Typography>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2">
              Exit date &nbsp;<strong>{showDate(form.exitDate)}</strong>
            </Typography>
            <Typography variant="body2">
              Reason &nbsp;
              <strong>
                {lookups.exitReasons.find((r) => r.id === Number(form.exitReasonId))?.name ||
                  'not given'}
              </strong>
            </Typography>
          </Box>
          <Typography variant="body2">
            Login &nbsp;
            <strong>
              {Number(form.loginStatus) === 1
                ? 'stays switched ON'
                : 'will be switched off'}
            </strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            After saving, this person stops appearing as a current employee and their
            employment record is closed on that date. If they return later it will be
            recorded as a rejoining, with a new joining date.
          </Typography>
          {Number(form.loginStatus) === 1 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Their login is still switched on, so they will be able to sign in after
              they have left. Set Login Status to Inactive unless they need access for
              a few more days.
            </Alert>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This cannot be undone from this screen.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doSave} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm exit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- popup 2, confirming a rejoin --------------------- */}
      <Dialog open={confirm === 'REJOIN'} onClose={cancelRejoin} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm rejoining</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{user.name}</strong> left on{' '}
            <strong>{showDate(employment.lastExitDate)}</strong>
            {employment.lastExitReason ? ` · ${employment.lastExitReason}` : ''}.
            You are recording that they are coming back.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            These will be <strong>cleared</strong> and you must enter them again:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2">
                Date of Joining, required, and it must be after{' '}
                {showDate(employment.lastExitDate)}
              </Typography>
            </li>
            <li><Typography variant="body2">Probation Completed On</Typography></li>
            <li><Typography variant="body2">Exit Date and Exit Reason</Typography></li>
          </Box>
          {/* What happens to the OLD employment depends on whether we can still
              rebuild it. With real history it is simply kept. With no history it is
              rebuilt from the user row, which only works while the joining date on
              that row still belongs to the first period. Where somebody has already
              reused the row for an earlier rejoin, the real start date is gone and
              all we can keep is a note. Saying so here beats a surprise afterwards. */}
          {/* THE EARLIER EMPLOYMENT, shown the same way every time.

              When this person has no history yet, the earlier employment is about
              to be CREATED, and it needs a start date. Prefilled from their record
              where that date can only belong to the first period, which is whenever
              it is earlier than the exit date. Left empty where it cannot, because
              somebody has already reused the row for a rejoin and the date on it is
              that rejoin date, not a start.

              Shown rather than used silently, so a wrong date gets caught by a
              human once, at the only moment anyone is thinking about it.

              When they DO have history, their earlier employment is already a real
              record with its own dates. Nothing to supply, so it is shown as it is
              and not offered for editing from here. */}
          {employment.historyEmpty ? (
            <>
              {!employment.canRebuildOldSpell && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Their <strong>original joining date is not recorded anywhere</strong>.
                    The date on their record,{' '}
                    {showDate(employment.joiningDateOnRow)}, is from a previous
                    rejoining, not from when they first started.
                  </Typography>
                </Alert>
              )}

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Their earlier employment started on *"
                InputLabelProps={{ shrink: true }}
                value={previousJoinedOn}
                onChange={(e) => setPreviousJoinedOn(e.target.value)}
                error={Boolean(
                  previousJoinedOn
                    && previousJoinedOn >= String(employment.lastExitDate).slice(0, 10),
                )}
                helperText={
                  previousJoinedOn
                    && previousJoinedOn >= String(employment.lastExitDate).slice(0, 10)
                    ? `It must be before ${showDate(employment.lastExitDate)}, the day they left`
                    : employment.canRebuildOldSpell
                      ? 'Already on their record. Correct it if it is wrong, or leave it as it is'
                      : `Required. It must be before ${showDate(employment.lastExitDate)}`
                }
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary">
                {previousJoinedOn ? (
                  <>
                    Earlier employment{' '}
                    <strong>
                      {showDate(previousJoinedOn)} to{' '}
                      {showDate(employment.lastExitDate)}
                    </strong>
                    , recorded as a closed record. The new one starts on the Date of
                    Joining you enter next.
                  </>
                ) : (
                  <>
                    This is <strong>required</strong>. Without it their earlier
                    employment cannot be recorded, and their history would say they
                    worked here once when they worked here twice.
                  </>
                )}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Their previous employment,{' '}
              <strong>
                {showDate(employment.lastJoiningDate)} to{' '}
                {showDate(employment.lastExitDate)}
              </strong>
              , is kept as it is, and the time between {showDate(employment.lastExitDate)}{' '}
              and the new joining date is recorded as time away.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRejoin}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyRejoin}
            disabled={
              // Only when the earlier employment is about to be created. Somebody
              // who already has history has nothing to supply.
              employment.historyEmpty
              && (!previousJoinedOn
                || previousJoinedOn >= String(employment.lastExitDate).slice(0, 10))
            }
          >
            Confirm rejoining
          </Button>
        </DialogActions>
      </Dialog>

    </GlobalWrapper>
  );
}
